import { MediaLayout, ReleaseNotePageMedia } from '../../../shared/models/release-note/release-note.model';

/** Wider than this (width / height) goes full width under AUTO. Exactly 2.8 still sits beside. */
export const WIDE_MEDIA_RATIO = 2.8;

/** Full-width media never grows taller than this, so a slide cannot push the pager off a short viewport. */
export const FULL_MEDIA_MAX_HEIGHT_PX = 240;

/**
 * Where a slide's media goes (handoff §7.4), from the **stored** dimensions so the box is sized before any
 * byte arrives. `FULL` / `BESIDE` are author overrides; `AUTO` derives from the ratio, and missing
 * dimensions — every WebP and every clip — fall back to full.
 */
export function resolveLayout(media: ReleaseNotePageMedia | null | undefined,
                              override: MediaLayout | null | undefined): 'full' | 'beside' {
  if (override === 'FULL') {
    return 'full';
  }
  if (override === 'BESIDE') {
    return 'beside';
  }
  const ratio = ratioOf(media);
  return ratio === null || ratio > WIDE_MEDIA_RATIO ? 'full' : 'beside';
}

/** Beside was chosen for a band wider than {@link WIDE_MEDIA_RATIO}: the pane shrinks to the image. */
export function isBesideWideBand(media: ReleaseNotePageMedia | null | undefined,
                                 override: MediaLayout | null | undefined): boolean {
  const ratio = ratioOf(media);
  return resolveLayout(media, override) === 'beside' && ratio !== null && ratio > WIDE_MEDIA_RATIO;
}

/** width / height, or null when either is missing. */
export function ratioOf(media: ReleaseNotePageMedia | null | undefined): number | null {
  if (!media?.width || !media?.height) {
    return null;
  }
  return media.width / media.height;
}

/** Whether a stored media item is a clip rather than a still. */
export function isVideoMedia(media: Pick<ReleaseNotePageMedia, 'contentType'> | null | undefined): boolean {
  return !!media?.contentType?.toLowerCase().startsWith('video/');
}
